import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Content,
  Progress,
  Table,
  TableColumn,
  InfoCard,
  StructuredMetadataTable,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Alert,
  AlertTitle,
  ToggleButton,
  ToggleButtonGroup,
} from '@material-ui/lab';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  Button,
  Collapse,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined';
import validator from '@rjsf/validator-ajv8';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import type { IChangeEvent } from '@rjsf/core';
import {
  VerticalTabNav,
  TabItemData,
  RjsfForm,
} from '@openchoreo/backstage-design-system';
import {
  DetailPageLayout,
  formatRelativeTime,
  YamlEditor,
  useYamlEditor,
} from '@openchoreo/backstage-plugin-react';
import { useWorkflowRuns } from '../../hooks/useWorkflowRuns';
import { useWorkflowRunDetails } from '../../hooks/useWorkflowRunDetails';
import { useWorkflowSchema } from '../../hooks/useWorkflowSchema';
import { useNamespaces } from '../../hooks/useNamespaces';
import { genericWorkflowsClientApiRef } from '../../api';
import { useSelectedNamespace } from '../../context';
import { WorkflowRunStatusChip } from '../WorkflowRunStatusChip';
import { WorkflowRunStepLogs } from '../WorkflowRunStepLogs';
import { WorkflowRunEvents } from '../WorkflowRunEvents';
import type { WorkflowRun } from '../../types';

type RunDetailsTab = 'logs' | 'events' | 'details';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
  title: {
    flexGrow: 1,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  tabContent: {
    marginTop: theme.spacing(2),
  },
  parametersContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    maxHeight: 400,
  },
  parametersCode: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  pollingIndicator: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontStyle: 'italic',
  },
  section: {
    marginTop: theme.spacing(3),
  },
  triggerSection: {
    marginBottom: theme.spacing(3),
  },
  triggerFormContainer: {
    marginTop: theme.spacing(2),
  },
  triggerActions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  noSchemaMessage: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  modeToggle: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  toggleButton: {
    textTransform: 'none',
    padding: theme.spacing(0.5, 2),
  },
  editorContainer: {
    minHeight: 320,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  namespaceSelector: {
    minWidth: 200,
    marginBottom: theme.spacing(2),
  },
}));

// Helper to calculate duration
function calculateDuration(createdAt: string, finishedAt?: string): string {
  if (!finishedAt) return '-';

  const start = new Date(createdAt).getTime();
  const end = new Date(finishedAt).getTime();
  const diffMs = end - start;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

// Helper to format date
function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
}

/**
 * Inline trigger form component shown within the runs page.
 */
type EditorMode = 'form' | 'yaml';

const TriggerForm = ({
  workflowName,
  namespaceName,
  workflowKind,
  onTriggered,
  onCancel,
}: {
  workflowName: string;
  /** The namespace in which to create the WorkflowRun. */
  namespaceName: string;
  workflowKind?: 'Workflow' | 'ClusterWorkflow';
  onTriggered: (runName: string) => void;
  onCancel: () => void;
}) => {
  const classes = useStyles();
  const client = useApi(genericWorkflowsClientApiRef);
  const { schema, loading, error } = useWorkflowSchema(
    workflowName,
    workflowKind,
  );

  const [mode, setMode] = useState<EditorMode>('form');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const yamlEditor = useYamlEditor({ initialContent: '' });

  const isPlainObject = (val: unknown): val is Record<string, unknown> =>
    val !== null && typeof val === 'object' && !Array.isArray(val);

  // Recursively build a params object from schema with defaults or type-appropriate empty values
  const buildParamsFromSchema = (
    schemaObj: unknown,
  ): Record<string, unknown> => {
    if (!isPlainObject(schemaObj)) return {};
    const props = schemaObj.properties;
    if (!isPlainObject(props)) return {};
    return Object.fromEntries(
      Object.entries(props).map(([key, propSchema]) => {
        if (isPlainObject(propSchema) && 'default' in propSchema) {
          return [key, propSchema.default];
        }
        const type = isPlainObject(propSchema) ? propSchema.type : undefined;
        if (type === 'object') {
          return [key, buildParamsFromSchema(propSchema)];
        }
        if (type === 'number' || type === 'integer') return [key, 0];
        if (type === 'boolean') return [key, false];
        if (type === 'array') return [key, []];
        return [key, ''];
      }),
    );
  };

  // Deep merge: schema defaults fill in missing/empty fields; formData values win otherwise
  const deepMergeWithDefaults = (
    defaults: Record<string, unknown>,
    override: Record<string, unknown>,
  ): Record<string, unknown> => {
    const result = { ...defaults };
    for (const [key, val] of Object.entries(override)) {
      // Skip rjsf-generated empty objects — they mean "no value entered", so keep the default
      if (isPlainObject(val) && Object.keys(val).length === 0) continue;
      if (isPlainObject(val) && isPlainObject(result[key])) {
        result[key] = deepMergeWithDefaults(
          result[key] as Record<string, unknown>,
          val,
        );
      } else {
        result[key] = val;
      }
    }
    return result;
  };

  const buildWorkflowRunManifest = (
    params: Record<string, unknown>,
  ): Record<string, unknown> => {
    // Omit params that are empty objects (rjsf initializes object-type fields
    // to {} when there's no default value — not useful to show in YAML)
    const meaningful = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) =>
          !(
            v !== null &&
            typeof v === 'object' &&
            !Array.isArray(v) &&
            Object.keys(v as object).length === 0
          ),
      ),
    );
    return {
      apiVersion: 'openchoreo.dev/v1alpha1',
      kind: 'WorkflowRun',
      metadata: {
        namespace: namespaceName,
      },
      spec: {
        workflow: {
          kind: workflowKind ?? 'Workflow',
          name: workflowName,
          ...(Object.keys(meaningful).length > 0
            ? { parameters: meaningful }
            : {}),
        },
      },
    };
  };

  const extractParametersFromYaml = (
    parsed: Record<string, unknown>,
  ): Record<string, unknown> | null => {
    if (parsed.kind === 'WorkflowRun' && isPlainObject(parsed.spec)) {
      const spec = parsed.spec as Record<string, unknown>;
      if (isPlainObject(spec.workflow)) {
        const workflow = spec.workflow as Record<string, unknown>;
        if ('parameters' in workflow) {
          if (!isPlainObject(workflow.parameters)) return null;
          return workflow.parameters;
        }
        return {};
      }
    }
    // Fallback: treat entire parsed object as raw parameters
    return parsed;
  };

  const switchToYaml = () => {
    const schemaDefaults = buildParamsFromSchema(schema);
    const merged = deepMergeWithDefaults(schemaDefaults, formData);
    yamlEditor.reset(buildWorkflowRunManifest(merged));
    setSchemaError(null);
    setMode('yaml');
  };

  const switchToForm = () => {
    if (yamlEditor.parseError) return;
    const parsed = yamlEditor.parseYaml();
    if (parsed) {
      if (!isPlainObject(parsed)) {
        setSchemaError('Invalid YAML: root must be a mapping/object');
        return;
      }
      const params = extractParametersFromYaml(parsed);
      if (params === null) {
        setSchemaError('Invalid YAML: parameters must be a mapping/object');
        return;
      }
      setFormData(params);
    } else {
      setFormData({});
    }
    setSchemaError(null);
    setMode('form');
  };

  const getParameters = (): Record<string, unknown> | null => {
    if (mode === 'yaml') {
      if (yamlEditor.parseError) return null;
      const parsed = yamlEditor.parseYaml();
      if (parsed === null) return {};
      if (!isPlainObject(parsed)) {
        setSchemaError('Invalid YAML: root must be a mapping/object');
        return null;
      }
      const params = extractParametersFromYaml(parsed);
      if (params === null) {
        setSchemaError('Invalid YAML: parameters must be a mapping/object');
        return null;
      }
      return params;
    }
    return formData;
  };

  const handleSubmit = async (data?: IChangeEvent) => {
    if (mode === 'yaml') {
      if (yamlEditor.parseError) return;
      const parameters = getParameters();
      if (parameters === null) return;

      if (schema && typeof schema === 'object' && 'properties' in schema) {
        const { errors } = validator.validateFormData(
          parameters,
          schema as RJSFSchema,
        );
        if (errors.length > 0) {
          setSchemaError(errors.map(e => e.stack).join('\n'));
          return;
        }
      }

      try {
        setSubmitting(true);
        setSubmitError(null);

        const run = await client.createWorkflowRun(
          namespaceName,
          workflowName,
          parameters,
          workflowKind,
        );

        onTriggered(run.name);
      } catch (err) {
        setSubmitError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const parameters = data ? data.formData ?? {} : getParameters() ?? {};
      const run = await client.createWorkflowRun(
        namespaceName,
        workflowName,
        parameters,
        workflowKind,
      );

      onTriggered(run.name);
    } catch (err) {
      setSubmitError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error loading workflow schema</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  const hasSchema =
    schema &&
    typeof schema === 'object' &&
    'properties' in schema &&
    Object.keys((schema as any).properties || {}).length > 0;

  const uiSchema: UiSchema = {
    'ui:submitButtonOptions': {
      norender: true,
    },
  };

  return (
    <InfoCard
      title="Trigger Workflow"
      action={
        <IconButton
          size="small"
          onClick={onCancel}
          title="Close"
          aria-label="Close workflow trigger"
        >
          <CloseIcon />
        </IconButton>
      }
    >
      {submitError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <AlertTitle>Error triggering workflow</AlertTitle>
          {submitError.message}
        </Alert>
      )}

      <Box className={classes.modeToggle}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_e, val) => {
            if (val === 'form') switchToForm();
            else if (val === 'yaml') switchToYaml();
          }}
          size="small"
        >
          <ToggleButton value="form" className={classes.toggleButton}>
            Form
          </ToggleButton>
          <ToggleButton value="yaml" className={classes.toggleButton}>
            YAML
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'form' ? (
        hasSchema ? (
          <Box className={classes.triggerFormContainer}>
            <RjsfForm
              schema={schema as RJSFSchema}
              uiSchema={uiSchema}
              formData={formData}
              onChange={e => setFormData(e.formData || {})}
              onSubmit={handleSubmit}
            >
              <Box className={classes.triggerActions}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? 'Triggering...' : 'Trigger Workflow'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Box>
            </RjsfForm>
          </Box>
        ) : (
          <Box>
            <Typography className={classes.noSchemaMessage}>
              This workflow has no configurable parameters.
            </Typography>
            <Box className={classes.triggerActions}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSubmit({ formData: {} } as IChangeEvent)}
                disabled={submitting}
              >
                {submitting ? 'Triggering...' : 'Trigger Workflow'}
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )
      ) : (
        <Box>
          <Box className={classes.editorContainer}>
            <YamlEditor
              content={yamlEditor.content}
              onChange={content => {
                yamlEditor.setContent(content);
                setSchemaError(null);
              }}
              errorText={yamlEditor.parseError || schemaError || undefined}
            />
          </Box>
          <Box className={classes.triggerActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmit()}
              disabled={submitting}
            >
              {submitting ? 'Triggering...' : 'Trigger Workflow'}
            </Button>
            <Button variant="outlined" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </InfoCard>
  );
};

/**
 * Sub-component: displays details + logs + events for a single run.
 */
const RunDetailView = ({
  runName,
  namespaceName,
  onBack,
}: {
  runName: string;
  /** The namespace the WorkflowRun lives in. Needed for ClusterWorkflow runs. */
  namespaceName?: string;
  onBack: () => void;
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<RunDetailsTab>('logs');

  const { run, loading, error, refetch } = useWorkflowRunDetails(
    runName,
    namespaceName,
  );

  const handleRefresh = () => {
    refetch();
  };

  const tabs = useMemo<TabItemData[]>(
    () => [
      {
        id: 'logs',
        label: 'Logs',
        icon: <DescriptionOutlinedIcon fontSize="small" />,
      },
      {
        id: 'events',
        label: 'Events',
        icon: <EventNoteOutlinedIcon fontSize="small" />,
      },
      {
        id: 'details',
        label: 'Details',
        icon: <InfoOutlinedIcon fontSize="small" />,
      },
    ],
    [],
  );

  if (loading && !run) {
    return <Progress />;
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error loading workflow run</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  if (!run) {
    return (
      <Alert severity="warning">
        <AlertTitle>Workflow run not found</AlertTitle>
        The workflow run "{runName}" could not be found.
      </Alert>
    );
  }

  const displayStatus = run.phase || run.status;

  const metadata = {
    Name: run.name,
    Status: <WorkflowRunStatusChip status={displayStatus} />,
    Workflow: run.workflowName,
    Namespace: run.namespaceName,
    Created: formatDate(run.createdAt),
    Finished: formatDate(run.finishedAt),
    UUID: run.uuid || '-',
  };

  const subtitle = (
    <>
      <WorkflowRunStatusChip status={displayStatus} />
      <Typography variant="body2" color="textSecondary">
        {run.createdAt ? formatRelativeTime(run.createdAt) : 'Unknown'}
      </Typography>
    </>
  );

  const actions = (
    <IconButton
      onClick={handleRefresh}
      size="small"
      title="Refresh"
      aria-label="Refresh"
    >
      <RefreshIcon />
    </IconButton>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'logs':
        return (
          <WorkflowRunStepLogs
            runName={runName}
            namespaceName={namespaceName}
          />
        );
      case 'events':
        return (
          <WorkflowRunEvents runName={runName} namespaceName={namespaceName} />
        );
      case 'details':
        return (
          <>
            <InfoCard title="Run Details">
              <StructuredMetadataTable metadata={metadata} />
            </InfoCard>
            {run.parameters && Object.keys(run.parameters).length > 0 && (
              <Box className={classes.section}>
                <InfoCard title="Parameters">
                  <Paper
                    className={classes.parametersContainer}
                    variant="outlined"
                  >
                    <pre className={classes.parametersCode}>
                      {JSON.stringify(run.parameters, null, 2)}
                    </pre>
                  </Paper>
                </InfoCard>
              </Box>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <DetailPageLayout
      title={run.name}
      subtitle={subtitle}
      onBack={onBack}
      actions={actions}
    >
      <VerticalTabNav
        tabs={tabs}
        activeTabId={activeTab}
        onChange={tabId => setActiveTab(tabId as RunDetailsTab)}
      >
        {renderTabContent()}
      </VerticalTabNav>
    </DetailPageLayout>
  );
};

/**
 * Entity-aware workflow runs content component.
 * Must be used within a NamespaceProvider.
 * Gets workflowName from the entity's metadata.name.
 * Shows a list of runs with the ability to trigger new runs and drill into run details/logs.
 * Supports both namespace-scoped Workflow and cluster-scoped ClusterWorkflow entities.
 *
 * For ClusterWorkflow entities a namespace selector dropdown is shown so the user
 * can choose which OpenChoreo namespace to query for WorkflowRun CRs.
 */
export const WorkflowRunsContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTriggerForm, setShowTriggerForm] = useState(false);

  const workflowName = entity.metadata.name;
  const workflowKind: 'Workflow' | 'ClusterWorkflow' =
    entity.kind?.toLowerCase() === 'clusterworkflow'
      ? 'ClusterWorkflow'
      : 'Workflow';
  const selectedRunName = searchParams.get('run');

  // For namespace-scoped workflows
  const contextNamespace = useSelectedNamespace();

  // For ClusterWorkflows
  const { namespaces, loading: namespacesLoading } = useNamespaces();
  const [selectedNamespace, setSelectedNamespace] = useState<string>('default');

  const [namespaceInitialised, setNamespaceInitialised] = useState(false);
  if (
    workflowKind === 'ClusterWorkflow' &&
    !namespacesLoading &&
    namespaces.length > 0 &&
    !namespaceInitialised
  ) {
    const preferred = namespaces.includes('default')
      ? 'default'
      : namespaces[0];
    setSelectedNamespace(preferred);
    setNamespaceInitialised(true);
  }

  // The namespace used for fetching runs:
  // - ClusterWorkflow: user-selected namespace from dropdown
  // - Workflow: namespace from context (entity annotation / entity.metadata.namespace)
  const runsNamespace =
    workflowKind === 'ClusterWorkflow' ? selectedNamespace : contextNamespace;

  const {
    runs,
    loading: runsLoading,
    error,
    refetch,
  } = useWorkflowRuns(workflowName, runsNamespace);

  const handleRunClick = (runName: string) => {
    setSearchParams({ run: runName });
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const handleTriggered = (runName: string) => {
    setShowTriggerForm(false);
    refetch();
    // Navigate to the newly triggered run
    setSearchParams({ run: runName });
  };

  // If a run is selected, show its details
  if (selectedRunName) {
    return (
      <Content>
        <RunDetailView
          runName={selectedRunName}
          namespaceName={runsNamespace}
          onBack={handleBackToList}
        />
      </Content>
    );
  }

  // Otherwise show the runs list
  const columns: TableColumn<WorkflowRun>[] = [
    {
      title: 'Run Name',
      field: 'name',
      render: (row: WorkflowRun) => (
        <Button
          color="primary"
          style={{ textTransform: 'none' }}
          onClick={e => {
            e.stopPropagation();
            handleRunClick(row.name);
          }}
        >
          {row.name}
        </Button>
      ),
    },
    {
      title: 'Status',
      field: 'status',
      render: (row: WorkflowRun) => (
        <WorkflowRunStatusChip status={row.phase || row.status} />
      ),
    },
    {
      title: 'Created',
      field: 'createdAt',
      defaultSort: 'desc',
      render: (row: WorkflowRun) => formatRelativeTime(row.createdAt || ''),
    },
    {
      title: 'Duration',
      render: (row: WorkflowRun) =>
        calculateDuration(row.createdAt, row.finishedAt),
    },
  ];

  return (
    <Content>
      {/* Namespace selector — only shown for ClusterWorkflow entities */}
      {workflowKind === 'ClusterWorkflow' && (
        <FormControl variant="outlined" className={classes.namespaceSelector}>
          <InputLabel id="namespace-select-label">Namespace</InputLabel>
          <Select
            labelId="namespace-select-label"
            label="Namespace"
            value={selectedNamespace}
            onChange={e => setSelectedNamespace(e.target.value as string)}
            disabled={namespacesLoading || namespaces.length === 0}
          >
            {namespaces.map(ns => (
              <MenuItem key={ns} value={ns}>
                {ns}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          Workflow Runs
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={() => setShowTriggerForm(prev => !prev)}
        >
          Trigger
        </Button>
        <IconButton
          onClick={() => refetch()}
          size="small"
          title="Refresh"
          aria-label="Refresh"
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Collapse in={showTriggerForm}>
        <Box className={classes.triggerSection}>
          <TriggerForm
            workflowName={workflowName}
            namespaceName={runsNamespace}
            workflowKind={workflowKind}
            onTriggered={handleTriggered}
            onCancel={() => setShowTriggerForm(false)}
          />
        </Box>
      </Collapse>

      {error && (
        <Alert severity="error">
          <AlertTitle>Error loading runs</AlertTitle>
          {error.message}
        </Alert>
      )}

      {runsLoading && <Progress />}

      {!runsLoading && runs.length === 0 && !showTriggerForm && (
        <Box className={classes.emptyState}>
          <Typography variant="h6">No runs yet</Typography>
          <Typography variant="body2">
            Click the "Trigger" button above to execute this workflow.
          </Typography>
        </Box>
      )}

      {!runsLoading && runs.length > 0 && (
        <Table
          data={runs}
          columns={columns}
          options={{
            search: true,
            paging: true,
            pageSize: 10,
            sorting: true,
          }}
          onRowClick={(_, row) => {
            if (row) {
              handleRunClick(row.name);
            }
          }}
        />
      )}
    </Content>
  );
};
